import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { showSuccess, showError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { incentiveService } from '@/services/incentiveService'
import { employeesService } from '@/services/employeesService'
import { accountManagementService } from '@/services/accountManagementService'
import type { IncentiveSavePayload } from '@/api/types/incentive.types'
import type { DepartmentLookup, Employee } from '@/api/types'
import type { Account } from '@/services/accountManagementService'

function getDefaultMonthYear(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export const IncentiveDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const isNew = id === 'new' || !id
  const editId = !isNew && id ? parseInt(id, 10) : 0

  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [employeesList, setEmployeesList] = useState<Employee[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [departmentId, setDepartmentId] = useState(0)
  const [employeeId, setEmployeeId] = useState<number | null>(null)
  const [monthYear, setMonthYear] = useState(getDefaultMonthYear())
  const [debitAccountId, setDebitAccountId] = useState(0)
  const [amount, setAmount] = useState('')
  const [details, setDetails] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const req = t('incentivePage.requiredMark')

  // Load departments (same as Attendance form – branch-wise: only when branch selected)
  useEffect(() => {
    if (!branchId) {
      setDepartments([])
      return
    }
    let cancelled = false
    employeesService.getDepartmentLookups().then((list) => {
      if (!cancelled && list) setDepartments(list)
    }).catch(() => { if (!cancelled) setDepartments([]) })
    return () => { cancelled = true }
  }, [branchId])

  // Load employees by branch (same as Attendance form)
  useEffect(() => {
    if (!branchId) {
      setEmployeesList([])
      return
    }
    let cancelled = false
    employeesService.getAllEmployees(branchId, 1, 500).then((res) => {
      if (cancelled) return
      const raw = res?.data ?? (res as any)?.Data
      setEmployeesList(Array.isArray(raw) ? raw : [])
    }).catch(() => { if (!cancelled) setEmployeesList([]) })
    return () => { cancelled = true }
  }, [branchId])

  // Load accounts for debit dropdown (same as other forms)
  useEffect(() => {
    if (!branchId) {
      setAccounts([])
      return
    }
    let cancelled = false
    accountManagementService
      .getAllAccounts(branchId)
      .then((list) => {
        if (!cancelled) setAccounts(list)
      })
      .catch(() => {
        if (!cancelled) setAccounts([])
      })
    return () => { cancelled = true }
  }, [branchId])

  // Load existing record in edit mode
  useEffect(() => {
    if (isNew || !branchId || !editId) return
    let cancelled = false
    setLoading(true)
    incentiveService
      .getById(editId, branchId)
      .then((data) => {
        if (!cancelled) {
          setEmployeeId(data.employeeId != null ? Number(data.employeeId) : 0)
          setDepartmentId(data.departmentId ?? 0)
          setMonthYear(data.monthYear ?? getDefaultMonthYear())
          setDebitAccountId(data.debitAccount ?? 0)
          setAmount(String(data.amount ?? ''))
          setDetails(data.details ?? '')
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err.response?.data?.message || t('incentivePage.loadFailed'))
          navigate('/payroll/incentives')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [isNew, branchId, editId, navigate])

  const departmentOptions = useMemo(
    () => [
      { value: '0', label: t('incentivePage.placeholderAll') },
      ...departments.filter((d) => d.id).map((d) => ({
        value: String(d.id),
        label: d.lookupNameEn || d.lookupNameAr || String(d.id),
      })),
    ],
    [departments, t]
  )

  const employeeOptions = useMemo(() => {
    const nameFor = (emp: Employee) =>
      [emp.empFirstNameEn, emp.empFamilyNameEn].filter(Boolean).join(' ').trim() || emp.empCode || '—'
    const list = departmentId
      ? employeesList.filter((e) => e.empDepartmetId === departmentId && e.id && e.empCode)
      : employeesList.filter((e) => e.id && e.empCode)
    return [
      { value: '0', label: t('incentivePage.allEmployees') },
      ...list.map((e) => ({ value: String(e.id), label: nameFor(e) })),
    ]
  }, [employeesList, departmentId, t])

  const debitAccountOptions = useMemo(
    () =>
      accounts.map((acc) => {
        const code = acc.accountCode || ''
        const name = acc.accountNameEn || acc.accountName || acc.accountNameAr || t('incentivePage.accountFallback', { id: acc.id })
        const label = code ? `${code} - ${name}` : name
        return { value: String(acc.id || 0), label }
      }),
    [accounts, t]
  )

  const handleSave = async () => {
    if (!branchId) {
      toast.error(t('incentivePage.toastBranch'))
      return
    }
    if (employeeId === null) {
      toast.error(t('incentivePage.toastEmployee'))
      return
    }
    if (!monthYear || monthYear.length < 7) {
      toast.error(t('incentivePage.toastMonthYear'))
      return
    }
    if (!debitAccountId) {
      toast.error(t('incentivePage.toastDebitAccount'))
      return
    }
    const amt = parseFloat(amount)
    if (Number.isNaN(amt) || amt < 0) {
      toast.error(t('incentivePage.toastAmount'))
      return
    }
    if (!details.trim()) {
      toast.error(t('incentivePage.toastDetails'))
      return
    }

    setSaving(true)
    try {
      const payload: IncentiveSavePayload = {
        id: isNew ? 0 : editId,
        empId: employeeId,
        amount: amt,
        monthYear,
        detail: details.trim(),
        departmentId: departmentId || 0,
        debitAccount: debitAccountId,
        branchId,
      }
      console.log('=== Incentive API Payload (Save/Update) ===')
      console.log(JSON.stringify(payload, null, 2))
      console.log('==========================================')
      if (isNew) {
        await incentiveService.save(payload)
        await showSuccess(t('incentivePage.savedTitle'), t('incentivePage.savedMessage'))
      } else {
        await incentiveService.update(payload)
        await showSuccess(t('incentivePage.updatedTitle'), t('incentivePage.updatedMessage'))
      }
      navigate('/payroll/incentives')
    } catch (err: any) {
      const data = err?.response?.data
      const msg =
        data?.result?.msg ??
        data?.Result?.msg ??
        data?.message ??
        err?.message ??
        t('incentivePage.saveFailed')
      await showError(t('incentivePage.saveFailed'), msg)
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('incentivePage.pageTitle')} description={t('incentivePage.pageDescription')} />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('incentivePage.selectBranchForm')}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title={t('incentivePage.pageTitle')} description={t('incentivePage.pageDescription')} />

      <Card>
        <CardHeader>
          <CardTitle>{t('incentivePage.cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">{t('incentivePage.loading')}</div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{req} {t('incentivePage.labelDepartment')}</Label>
                  <SearchableSelect
                    options={departmentOptions}
                    value={departmentId ? String(departmentId) : '0'}
                    onValueChange={(v) => {
                      setDepartmentId(v ? Number(v) : 0)
                      setEmployeeId(null)
                    }}
                    placeholder={t('incentivePage.placeholderAll')}
                    searchPlaceholder={t('incentivePage.searchShort')}
                    emptyMessage={t('incentivePage.emptyDepartment')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{req} {t('incentivePage.labelEmployee')}</Label>
                  <SearchableSelect
                    options={employeeOptions}
                    value={employeeId === null ? '' : String(employeeId)}
                    onValueChange={(v) => setEmployeeId(v === '' ? null : Number(v))}
                    placeholder={t('incentivePage.placeholderSelectEmployee')}
                    searchPlaceholder={t('incentivePage.searchShort')}
                    emptyMessage={t('incentivePage.emptyEmployee')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{req} {t('incentivePage.labelMonthYear')}</Label>
                  <Input
                    type="month"
                    value={monthYear}
                    onChange={(e) => setMonthYear(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{req} {t('incentivePage.labelDebitAccount')}</Label>
                  <SearchableSelect
                    options={debitAccountOptions}
                    value={debitAccountId ? String(debitAccountId) : ''}
                    onValueChange={(v) => setDebitAccountId(v ? Number(v) : 0)}
                    placeholder={t('incentivePage.placeholderSelectAccount')}
                    searchPlaceholder={t('incentivePage.searchAccounts')}
                    emptyMessage={t('incentivePage.emptyAccounts')}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{req} {t('incentivePage.labelAmount')}</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder={t('incentivePage.placeholderAmount')}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{req} {t('incentivePage.labelDetails')}</Label>
                <Textarea
                  placeholder={t('incentivePage.placeholderDetails')}
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  rows={4}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => navigate('/payroll/incentives')}>
                  {t('incentivePage.cancel')}
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? t('incentivePage.saving') : t('incentivePage.save')}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
